import { useRouter } from 'next/router';

import EditOrgSettingsSkeleton from 'screens/organizations/components/OrganizationSettings/components/EditOrgSettingsSkeleton';
import ScreenTopBar from 'legacy/components/ScreenTopBar';
import {
  useGetOrganizationMemberships,
  useExportOrganizationMembershipsCSV
} from 'api/queries/organizations/memberships';
import { useOrganization } from 'screens/organizations/context/EditOrganizationProvider';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import Button from 'components/Button';
import MembershipTierCard from './components/MembershipTierCard';
import { useTranslation } from 'react-i18next';
import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import { WEBVIEW_CLOSE } from 'legacy/common/constants/webview';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';

function MembershipsManageList() {
  const router = useRouter();
  const { t } = useTranslation(['organizations']);
  const { data } = useOrganization();
  const isWebview = isBounceWebview(router.query);

  const { data: membershipTiers, isLoading: isLoadingMembershipTiers } =
    useGetOrganizationMemberships({
      organizationId: data.organization.id
    });

  const { isLoading: isExporting, refetch: exportCSV } =
    useExportOrganizationMembershipsCSV({
      organizationId: data.organization.id,
      enabled: false
    });

  const handleBack = () => {
    if (isWebview) {
      postMessageToRN(WEBVIEW_CLOSE);
    } else {
      router.back();
    }
  };

  const handleEditOffer = (membershipTierId: string) => {
    router.push(
      `/organizations/${data.organization.id}/edit/settings/memberships/${membershipTierId}`
    );
  };

  const handleCreateOffer = () => {
    router.push(
      `/organizations/${data.organization.id}/edit/settings/memberships/create`
    );
  };

  const onClickExportCSV = async () => {
    try {
      const result = await exportCSV();
      if (result.data) {
        const today = new Date().toISOString().split('T')[0]; // yyyy-mm-dd format
        saveAs(result.data, `organization-memberships-${today}.csv`);
      }
    } catch (error) {
      toast.error(t('members.exportMembershipsFailed'));
    }
  };

  if (data.isLoadingOrganization || isLoadingMembershipTiers) {
    return <EditOrgSettingsSkeleton />;
  }

  return (
    <>
      <ScreenTopBar
        title={t(
          'organizations:memberships.membershipsTiers.membershipTiersTitle'
        )}
        onBack={handleBack}
        subtitle={data.organizationData?.name}
      />
      <FadeIn>
        <div className="pt-[36x] md:pt-[90px] -mt-[36px] pb-20 md:w-[630px] md:mx-auto md:px-0 px-4">
          <div className="my-4">
            <div className="flex flex-col gap-4">
              {membershipTiers?.length === 0 && (
                <div className="mt-4 flex flex-col items-center gap-2 max-w-[343px] mx-auto">
                  <img
                    src="/img/illustrations/empty-memberships-placeholder.png"
                    className="w-[254px] object-cover ml-[70px]"
                    alt="No membership tiers found"
                  />
                  <h2 className="text-xl font-bold text-center">
                    {t(
                      'organizations:memberships.membershipsTiers.noMemberships'
                    )}
                  </h2>
                  <p className="text-center text-sm font-normal text-gray-500">
                    {t(
                      'organizations:memberships.membershipsTiers.noMembershipsDescription'
                    )}
                  </p>
                </div>
              )}
              {membershipTiers?.map((tier) => (
                <div className="mt-4" key={tier.product.id}>
                  <MembershipTierCard tier={tier} onClick={handleEditOffer} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </FadeIn>
      <div className="w-full bg-white fixed bottom-0 left-0 p-4 z-10 border-t pb-12 md:pb-4">
        <div className="w-full md:w-[600px] mx-auto">
          <div className="flex gap-4 justify-center">
            <Button
              variant="secondary"
              onClick={onClickExportCSV}
              size="xlarge"
              className="flex-1"
              loading={isExporting}
            >
              {t('memberships.membershipsTiers.downloadMembershipCSV')}
            </Button>
            <Button
              variant="primaryDark"
              onClick={handleCreateOffer}
              size="xlarge"
              className="flex-1"
            >
              {t('memberships.membershipsTiers.createMembershipTier')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default MembershipsManageList;
